export type AnalyticsPageType = 'marketing' | 'auth' | 'learn' | 'payments'

export const analyticsSchemaVersion = 2

export type AnalyticsEventName =
	| 'signup_started'
	| 'signup_verification_sent'
	| 'signup_completed'
	| 'signup_validation_failed'
	| 'onboarding_validation_failed'
	| 'verification_failed'
	| 'verification_resend_clicked'
	| 'onboarding_started'
	| 'onboarding_completed'
	| 'login_started'
	| 'login_completed'
	| 'login_failed'
	| 'pricing_viewed'
	| 'billing_checkout_started'
	| 'upgrade_moment_shown'
	| 'upgrade_moment_cta_clicked'
	| 'facet_degustacion_used'
	| 'billing_payment_succeeded'
	| 'subscription_activated'
	| 'billing_payment_cancelled'
	| 'course_detail_viewed'
	| 'course_detail_mobile_sticky_cta_shown'
	| 'course_detail_mobile_sticky_cta_clicked'
	| 'collection_detail_viewed'
	| 'course_enrollment_started'
	| 'course_enrollment_completed'
	| 'course_enrollment_failed'
	| 'collection_enrollment_started'
	| 'collection_enrollment_completed'
	| 'collection_enrollment_failed'
	| 'dashboard_viewed'
	| 'dashboard_scope_filters_changed'
	| 'course_instance_viewed'
	| 'topic_viewed'
	| 'topic_completed'
	| 'topic_resumed'
	| 'topic_web_vitals'
	| 'resource_viewed'
	| 'resource_opened_inline'
	| 'resource_bookmarked'
	| 'note_created'
	| 'docs_page_view'
	| 'docs_scroll_depth'
	| 'docs_semantic_search'
	| 'docs_semantic_search_result_click'

type EventProps = Record<string, unknown>

/**
 * Stable per-field failure codes for `onboarding_validation_failed`, mapped
 * from zod issue codes (never from translated message text).
 */
export type OnboardingValidationFailureReason =
	| 'required'
	| 'too_short'
	| 'too_long'
	| 'invalid_format'
	| 'mismatch'
	| 'username_exists'
	| 'password_too_common'
	| 'not_accepted'
	| 'invalid'

export type AnalyticsPropertiesMap = {
	signup_started: {
		source?: string
	}
	signup_verification_sent: {
		method?: 'email'
		source?: string
	}
	signup_completed: {
		method?: 'email' | 'oauth'
		source?: string
	}
	onboarding_started: {
		entry?: 'verification' | 'oauth'
	}
	/**
	 * PRD 038 friction events. Never attach the email, name, or any entered
	 * value: only the normalized `field` / `reason` / `type` codes below.
	 */
	signup_validation_failed: {
		field: 'email'
		reason: 'required' | 'invalid_format' | 'email_exists'
	}
	onboarding_validation_failed: {
		field: 'username' | 'name' | 'password' | 'confirm_password' | 'terms'
		reason: OnboardingValidationFailureReason
	}
	verification_failed: {
		reason: 'invalid_code' | 'expired_or_missing' | 'token_mismatch'
	}
	verification_resend_clicked: {
		type: 'onboarding' | 'reset-password'
	}
	onboarding_completed: {
		duration_seconds?: number
	}
	login_started: {
		method: 'email' | 'oauth'
	}
	login_completed: {
		method: 'email' | 'oauth' | 'credentials'
	}
	login_failed: {
		method: 'email' | 'oauth'
		error_code?: string
	}
	pricing_viewed: {
		billing_enabled: boolean
		authenticated: boolean
		source_surface: string
	}
	billing_checkout_started: {
		plan_id: string
		price: number
		currency: string
		billing_cycle?: string
	}
	/**
	 * PRD 040 upgrade funnel. `surface` is a stable snake_case id of the UI
	 * spot that opened the dialog (e.g. 'topic_actions_menu'); no PII.
	 */
	upgrade_moment_shown: {
		variant: 'facet' | 'resources' | 'generic' | 'budget'
		surface: string
	}
	upgrade_moment_cta_clicked: {
		variant: 'facet' | 'resources' | 'generic' | 'budget'
		surface: string
	}
	facet_degustacion_used: {
		course_id: string
		topic_id: string
	}
	billing_payment_succeeded: {
		session_id?: string | null
		subscription_active: boolean
	}
	subscription_activated: {
		subscription_id: string
		plan_id: string
		amount: number
		currency: string
		source: 'polar_webhook'
	}
	billing_payment_cancelled: {
		source: 'checkout'
	}
	course_detail_viewed: {
		course_id: string
		course_slug: string
	}
	course_detail_mobile_sticky_cta_shown: {
		course_id: string
		course_slug: string
		cta_state: 'anonymous' | 'logged_in' | 'enrolled'
		scroll_context?: string
	}
	course_detail_mobile_sticky_cta_clicked: {
		course_id: string
		course_slug: string
		cta_state: 'anonymous' | 'logged_in' | 'enrolled'
		scroll_context?: string
	}
	collection_detail_viewed: {
		collection_id: string
		collection_slug: string
	}
	course_enrollment_started: {
		course_id: string
		course_slug: string
	}
	course_enrollment_completed: {
		course_id: string
		course_slug: string
	}
	course_enrollment_failed: {
		course_id: string
		/** Optional: surfaces like the private program page only know the id. */
		course_slug?: string
		error_code?: string
	}
	collection_enrollment_started: {
		collection_id: string
		collection_slug?: string
	}
	collection_enrollment_completed: {
		collection_id: string
		collection_slug?: string
	}
	collection_enrollment_failed: {
		collection_id: string
		collection_slug?: string
		error_code?: string
	}
	dashboard_viewed: EventProps
	dashboard_scope_filters_changed: {
		scope: 'panel' | 'catalog'
		type_filter: string
		status_filter: string
		has_category_filter: boolean
		query_length: number
		has_query: boolean
	}
	course_instance_viewed: {
		course_id: string
	}
	topic_viewed: {
		course_id: string
		topic_id: string
	}
	topic_completed: {
		course_id: string
		topic_id: string
	}
	/** Study surface: the learner follows «Te quedaste aquí» back to a section. */
	topic_resumed: {
		course_id: string
		topic_id: string
		section_position: number
	}
	/**
	 * PRD 041 field measurement: exactly the fields CoreWebVitals provides
	 * (no INP). Joined with topic_viewed in PostHog via course_id/topic_id.
	 */
	topic_web_vitals: {
		course_id: string
		topic_id: string
		lcp: number
		cls: number
		fcp?: number
		ttfb?: number
	}
	resource_viewed: {
		course_id: string
		resource_id: string
		topic_id?: string
	}
	/** Study surface: a resource opened inside the topic, without leaving it. */
	resource_opened_inline: {
		course_id: string
		resource_id: string
		topic_id: string
		resource_type: string
	}
	resource_bookmarked: {
		resource_id: string
		course_id?: string
		topic_id?: string
	}
	note_created: {
		entity_type: 'course' | 'topic' | 'resource'
		entity_id: string
		facet_id: string
	}
	docs_page_view: {
		path: string
		title: string
	}
	docs_scroll_depth: {
		path: string
		depth: number
	}
	docs_semantic_search: {
		query: string
		result_count: number
	}
	docs_semantic_search_result_click: {
		query: string
		path: string
	}
}

export type AnalyticsEventPayload<
	Name extends AnalyticsEventName = AnalyticsEventName,
> = {
	name: Name
	properties?: AnalyticsPropertiesMap[Name]
}

/** Event returned by a successful server action and consumed by the root observer. */
export type AnalyticsActionResult = {
	analyticsEvent?: AnalyticsEventPayload
}

export type AnalyticsIdentifyPayload = {
	userId: string
	traits?: Record<string, unknown>
}

export type AnalyticsPageViewPayload = {
	pageType: AnalyticsPageType
	properties?: Record<string, unknown>
}
